import React, {
  ChangeEvent,
  FC,
  Suspense,
  useCallback,
  useEffect,
  useState
} from 'react';

import { Avatar, Button, Switch } from '@mui/material';
import { translate, translateDynamic } from '@utils/locale';
import { EmptySearchState } from '@assets/icons';
import { ProcessTableDataMap } from '@components/custom-table/types';
import { Order } from '@components/table/types';
import { deepOrange } from '@mui/material/colors';
import {
  ActionButton,
  MaterialReactTable,
  CustomTab,
  EllipsisTooltipText,
  SearchBar
} from '@components';
import { API_RESPONSE_STATUS } from '@constants/common';

import { InvestorsTableLimit, InvestorType } from '../constants';
import { InvestorsTableProps } from '../types';
import useGetInvestors, {
  InvestorsTableData,
  InvestorsTableHeader
} from '../hooks/useGetInvestors';
import AddAngelInvestorModal from './AddAngelInvestorModal';
import AddInstituitionalInvestorModal from './AddInstituitionalInvestorModal';
import AddButton from '../components/add-button/AddButton';
import DeleteInvestorModal from '../components/delete-investor-modal/DeleteInvestorModal';

const InvestorsTable: FC<InvestorsTableProps> = ({
  showVisibleDealColumn,
  showEmptyState,
  roundId,
  disableEdit
}) => {
  const [selectedTab, setSelectedTab] = useState(
    InvestorType.InstitutionalInvestor
  );
  const [
    isAddInstituitionalInvestorModal,
    setIsAddInstituitionalInvestorModal
  ] = useState<boolean>(false);
  const [isAddAngelInvestorModalOpen, seIsAddAngelInvestorModalOpen] =
    useState<boolean>(false);
  const [memberIdToEdit, setMemberIdToEdit] = useState('');
  const [memberIdToDelete, setMemberIdToDelete] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  const {
    sortInstitutionalOrder,
    setSortInstitutionalOrder,
    sortInstitutionalBy,
    setSortInstitutionalBy,
    institutionalLimit,
    institutionalOffset,
    setInstitutionalLimit,
    setInstitutionalOffset,
    setInstitutionalSearchTerm,
    institutionalSearchTerm,
    institutionalInvestorsData: formattedInstituitionalData,
    totalInstitutionalRows,
    isInstitutionalLoading,
    deleteInvestorLoading,
    sortAngelOrder,
    setSortAngelOrder,
    sortAngelBy,
    setSortAngelBy,
    angelLimit,
    angelOffset,
    setAngelLimit,
    setAngelOffset,
    setAngelSearchTerm,
    angelSearchTerm,
    angelInvestorsData: formattedAngelsData,
    totalAngelRows,
    isAngelLoading,
    handleDeleteInvestor,
    deleteInvestorResponse,
    handleVisibleInDealChange,
    maxInstitutionalOrder,
    maxAngelOrder
  } = useGetInvestors(roundId);

  const INVESTOR_LIST_ACTIONS = [
    {
      id: 'edit',
      label: translate('company.investorsTable.edit')
    },
    {
      id: 'delete',
      label: translate('company.investorsTable.delete')
    }
  ];

  const headerCells = [
    {
      id: 'no',
      label: 'No.',
      disableSort: true,
      showColumn: true,
      initialWidth: 40
    },
    {
      id: 'name',
      label: translate('company.investorsTable.name'),
      showColumn: true,
      disableSort: true
    },
    {
      id: 'url',
      label: translate('company.investorsTable.url'),
      disableSort: true,
      showColumn: true
    },
    ...(selectedTab === InvestorType.InstitutionalInvestor
      ? []
      : [
          {
            id: 'designation',
            label: translate('company.investorsTable.designation'),
            disableSort: true,
            showColumn: true
          }
        ]),
    // {
    //   id: 'visibleInDeal',
    //   label: translate('company.investorsTable.visibleInDeal'),
    //   disableSort: true,
    //   showColumn: showVisibleDealColumn
    // },

    ...(disableEdit
      ? []
      : [
          {
            id: 'actions',
            label: translate('company.investorsTable.actions'),
            disableSort: true,
            showColumn: true
          }
        ])
  ];

  const tabOptions = [
    {
      id: InvestorType.InstitutionalInvestor,
      label: translateDynamic(
        `company.investorsTable.${InvestorType.InstitutionalInvestor}`
      ),
      count: totalInstitutionalRows || 0
    },
    {
      id: InvestorType.SuperAngelInvestor,
      label: translateDynamic(
        `company.investorsTable.${InvestorType.SuperAngelInvestor}`
      ),
      count: totalAngelRows || 0
    }
  ];

  const tableHeaders = headerCells.filter(item => item.showColumn === true);

  const investorTableData =
    selectedTab === InvestorType.InstitutionalInvestor
      ? formattedInstituitionalData
      : formattedAngelsData;

  const investorDetail = memberIdToEdit
    ? investorTableData.find(item => item.id === memberIdToEdit)
    : undefined;

  const handleTabChange = (optionId: InvestorType) => {
    setSelectedTab(optionId);
  };

  const handleMenuItemClick = (
    optionId: string,
    teamMemberId: string,
    closeMenu: () => void
  ) => {
    if (optionId === 'edit') {
      setMemberIdToEdit(teamMemberId);
      handleOpenModal(selectedTab);
    } else if (optionId === 'delete') {
      setIsDeleteModalOpen(true);
      setMemberIdToDelete(teamMemberId);
    }
    closeMenu();
  };

  const onVisibleInDealChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InvestorsTableData
  ) => {
    handleVisibleInDealChange(event.target.checked, data, data.id);
  };

  const processTableData = useCallback(
    (
      data: Record<string, string | number | boolean>,
      index: number,
      closeMenu: () => void
    ): ProcessTableDataMap => {
      return {
        no: <div className="text-center">{index + 1}</div>,

        name: (
          <div className="flex gap-2 items-center">
            <div>
              {data.logoPath ? (
                <Avatar
                  sx={{ bgcolor: deepOrange[500], width: 24, height: 24 }}
                  src={(data.logoPath as string) ?? ''}
                  variant="square"
                />
              ) : (
                <Avatar variant="square" sx={{ width: 24, height: 24 }}>
                  {String(data.name).charAt(0).toUpperCase()}
                </Avatar>
              )}
            </div>
            <div>{data.name}</div>
          </div>
        ),

        url: <EllipsisTooltipText text={String(data?.url || '-')} />,

        designation: (
          <EllipsisTooltipText text={String(data?.designation || '-')} />
        ),

        visibleInDeal: (
          <Switch
            checked={!!data.visibleInDeal}
            onChange={
              showVisibleDealColumn
                ? event =>
                    onVisibleInDealChange(event, data as InvestorsTableData)
                : () => {}
            }
            inputProps={{ 'aria-label': 'controlled' }}
          />
        ),

        actions: (
          <ActionButton
            handleMenuItemClick={option => {
              handleMenuItemClick(option, data.id as string, closeMenu);
            }}
            label={translate('company.investorsTable.actions')}
            options={INVESTOR_LIST_ACTIONS}
            renderEllipsis={true}
            hideDivider
            isDisabled={disableEdit}
            returnMenuItemsOnly
          />
        )
      };
    },
    [selectedTab]
  );

  const InvestorTableEmptyState = (
    <div className="flex flex-col gap-2 justify-center w-full h-[300px]">
      {disableEdit ? (
        <div className="flex justify-center">
          <EmptySearchState />
        </div>
      ) : (
        <>
          <div className="flex justify-center">
            <EmptySearchState />
          </div>
          {(selectedTab === InvestorType.InstitutionalInvestor
            ? institutionalSearchTerm
            : angelSearchTerm) && (
            <div className="text-center">
              {translate('company.investorsTable.emptyState', {
                searchTerm:
                  selectedTab === InvestorType.InstitutionalInvestor
                    ? institutionalSearchTerm
                    : angelSearchTerm
              })}
            </div>
          )}
          <div className="flex gap-4 justify-center">
            <Button
              variant="contained"
              onClick={() =>
                handleOpenModal(InvestorType.InstitutionalInvestor)
              }>
              {translate('company.investorsTable.createInstInvestor')}
            </Button>
            <Button
              variant="contained"
              onClick={() => handleOpenModal(InvestorType.SuperAngelInvestor)}>
              {translate('company.investorsTable.createAngelInvestor')}
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const handleOffsetChange = (newOffset: number) => {
    if (selectedTab === InvestorType.InstitutionalInvestor)
      setInstitutionalOffset(newOffset);
    else setAngelOffset(newOffset);
  };

  const resetInstitutionalOffset = () => setInstitutionalOffset(0);

  const resetAngelOffset = () => setAngelOffset(0);

  const handleLimitChange = (newLimit: number) => {
    if (selectedTab === InvestorType.InstitutionalInvestor)
      setInstitutionalLimit(newLimit);
    else setAngelLimit(newLimit);
  };

  const handleChangeOrder = (newOrder: Order) => {
    if (selectedTab === InvestorType.InstitutionalInvestor)
      setSortInstitutionalOrder(newOrder);
    else setSortAngelOrder(newOrder);
  };

  const handleSort = (value: InvestorsTableHeader) => {
    if (selectedTab === InvestorType.InstitutionalInvestor)
      setSortInstitutionalBy(value);
    else setSortAngelBy(value);
  };

  const handleRowClick = () => {};

  const onClickShowMore = () => {
    if (selectedTab === InvestorType.InstitutionalInvestor)
      handleOffsetChange(institutionalOffset + InvestorsTableLimit);
    else handleOffsetChange(angelOffset + InvestorsTableLimit);
  };

  const handleOpenModal = (investorType: InvestorType) => {
    if (investorType === InvestorType.InstitutionalInvestor)
      setIsAddInstituitionalInvestorModal(true);
    if (investorType === InvestorType.SuperAngelInvestor)
      seIsAddAngelInvestorModalOpen(true);
  };

  const handleCloseModal = (investorType: InvestorType) => {
    if (investorType === InvestorType.InstitutionalInvestor)
      setIsAddInstituitionalInvestorModal(false);
    if (investorType === InvestorType.SuperAngelInvestor)
      seIsAddAngelInvestorModalOpen(false);
    setMemberIdToEdit('');
  };

  useEffect(() => {
    if (deleteInvestorResponse?.status === API_RESPONSE_STATUS.OK) {
      setMemberIdToDelete('');
      setIsDeleteModalOpen(false);
    }
  }, [deleteInvestorResponse]);

  return (
    <div>
      <div className="pb-5 mt-14  text-xl font-semibold leading-8">
        {translate('company.investorsTable.investors')}
      </div>
      <div className="flex justify-between pb-4">
        <div className="flex flex-row gap-4 w-[55%]">
          <SearchBar
            onSearch={
              selectedTab === InvestorType.InstitutionalInvestor
                ? setInstitutionalSearchTerm
                : setAngelSearchTerm
            }
            placeholder={translate('dealListingPage.search')}
            value={
              selectedTab === InvestorType.InstitutionalInvestor
                ? institutionalSearchTerm
                : angelSearchTerm
            }
          />
        </div>
      </div>
      <div className="flex justify-between pb-3">
        <CustomTab
          options={tabOptions}
          handleTabClick={handleTabChange}
          value={selectedTab}
        />
        {!!investorTableData.length && !disableEdit && (
          <div className="flex gap-4 items-center">
            <AddButton
              handleButtonClick={() =>
                handleOpenModal(InvestorType.InstitutionalInvestor)
              }
              label={translate('company.investorsTable.addInstInvestor')}
            />
            <AddButton
              handleButtonClick={() =>
                handleOpenModal(InvestorType.SuperAngelInvestor)
              }
              label={translate('company.investorsTable.addAngelInvestor')}
            />
          </div>
        )}
      </div>

      <div className="mt-2">
        <Suspense fallback={<></>}>
          <MaterialReactTable
            tableHeaders={tableHeaders}
            tableData={investorTableData}
            processTableData={processTableData}
            // customContainerStyles="border border-b-0 max-h-[1000px]"
            elevation={0}
            disablePagination
            renderEmptyRowsFallback={
              showEmptyState ? () => InvestorTableEmptyState : undefined
            }
            isLoading={isInstitutionalLoading || isAngelLoading}
            page={
              selectedTab === InvestorType.InstitutionalInvestor
                ? Math.floor(institutionalOffset / institutionalLimit) ?? 0
                : Math.floor(angelOffset / angelLimit) ?? 0
            }
            rowsPerPage={
              selectedTab === InvestorType.InstitutionalInvestor
                ? institutionalLimit ?? 0
                : angelLimit ?? 0
            }
            handleLimitChange={handleLimitChange}
            handleOffsetChange={handleOffsetChange}
            order={
              selectedTab === InvestorType.InstitutionalInvestor
                ? sortInstitutionalOrder
                : sortAngelOrder
            }
            handleChangeOrder={handleChangeOrder}
            handleOrderBy={handleSort}
            orderBy={
              selectedTab === InvestorType.InstitutionalInvestor
                ? sortInstitutionalBy
                : sortAngelBy
            }
            totalRows={
              selectedTab === InvestorType.InstitutionalInvestor
                ? totalInstitutionalRows ?? 0
                : totalAngelRows ?? 0
            }
            isRowClickable={true}
            handleRowClick={handleRowClick}
            disableColumnActionMenu
            disableScrollToTop
          />
        </Suspense>
        {(selectedTab === InvestorType.InstitutionalInvestor
          ? totalInstitutionalRows || 0
          : totalAngelRows || 0) > investorTableData.length && (
          <div className="flex justify-center w-full">
            <Button
              variant="text"
              onClick={onClickShowMore}
              disabled={
                investorTableData.length ===
                (selectedTab === InvestorType.InstitutionalInvestor
                  ? totalInstitutionalRows
                  : totalAngelRows)
              }>
              {translate('showMore')}
            </Button>
          </div>
        )}
      </div>
      {isAddAngelInvestorModalOpen && (
        <AddAngelInvestorModal
          handleCloseModal={() => {
            handleCloseModal(InvestorType.SuperAngelInvestor);
          }}
          isOpen={isAddAngelInvestorModalOpen}
          setModalOpen={seIsAddAngelInvestorModalOpen}
          memberDetails={
            (memberIdToEdit && isAddAngelInvestorModalOpen
              ? investorDetail
              : []) as InvestorsTableData
          }
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          roundId={roundId}
          disableChooseInvestor={!!memberIdToEdit}
          resetAngelOffset={resetAngelOffset}
          maxOrder={maxAngelOrder ?? 0}
        />
      )}
      {isAddInstituitionalInvestorModal && (
        <AddInstituitionalInvestorModal
          handleCloseModal={() => {
            handleCloseModal(InvestorType.InstitutionalInvestor);
          }}
          isOpen={isAddInstituitionalInvestorModal}
          setModalOpen={setIsAddInstituitionalInvestorModal}
          memberDetails={
            (memberIdToEdit && isAddInstituitionalInvestorModal
              ? investorDetail
              : []) as InvestorsTableData
          }
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          roundId={roundId}
          disableChooseInvestor={!!memberIdToEdit}
          resetInstitutionalOffset={resetInstitutionalOffset}
          maxOrder={maxInstitutionalOrder ?? 0}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteInvestorModal
          handleCancelClick={() => setIsDeleteModalOpen(false)}
          investorName={investorDetail?.name ?? ''}
          isModalOpen={isDeleteModalOpen}
          onClickDeleteInvestor={() => {
            handleDeleteInvestor(memberIdToDelete || memberIdToEdit);
          }}
          isDeleteLoading={deleteInvestorLoading}
          investorType={selectedTab}
        />
      )}
    </div>
  );
};

export default InvestorsTable;
